import { CommonModule } from '@angular/common';
import { Component, DestroyRef, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { Router } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { forkJoin, Subscription } from 'rxjs';
import { finalize } from 'rxjs/operators';
import { ToastService } from '../services/notification/toast.service';
import { PartnerService } from '../services/partner/partner.service';
import { SecurityService } from '../services/security.service';
import { SpinnerService } from '../services/spinner/spinner.service';

@Component({
  selector: 'app-admin-create-partner',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    TranslateModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatIconModule,
    MatButtonModule
  ],
  templateUrl: './admin-create-partner.component.html',
  styleUrls: ['./admin-create-partner.component.scss']
})
export class AdminCreatePartnerComponent implements OnInit {
  isSuperAdmin = false;
  partnerForm: FormGroup;

  banks: any[] = [];
  countries: any[] = [];
  cities: any[] = [];
  currencies: any[] = [];
  whatsappNumbers: any[] = [];
  private citiesSubscription?: Subscription;

  constructor(
    private partnerService: PartnerService,
    private securityService: SecurityService,
    private toast: ToastService,
    private spinner: SpinnerService,
    private router: Router,
    private translate: TranslateService,
    private destroyRef: DestroyRef
  ) {
    this.partnerForm = new FormGroup({
      name_partner: new FormControl('', Validators.required),
      phone_partner: new FormControl('', Validators.required),
      adress_partner: new FormControl('', Validators.required),
      country: new FormControl('', Validators.required),
      city: new FormControl('', Validators.required),
      currency_id: new FormControl('', Validators.required),
      status_partner: new FormControl(1, Validators.required),
      bank: new FormControl('', Validators.required),
      whatsappNumber: new FormControl(''),
      email_partner: new FormControl('noreply@billcentrix.com', [Validators.required, Validators.email]),
      access_email: new FormControl('', [Validators.required, Validators.email]),
      feeRate_partner: new FormControl(1),
      name_portal: new FormControl('', Validators.required),
      sgSenderId: new FormControl('4591613', Validators.required),
      acummulate_debt: new FormControl(false, Validators.required),
      create_email_notification: new FormControl(false, Validators.required)
    });
  }

  ngOnInit(): void {
    this.isSuperAdmin = this.securityService.isSuperAdmin();
    if (!this.isSuperAdmin) {
      return;
    }

    this.loadCatalogs();
  }

  onCountryChange(countryId: number): void {
    if (!this.isSuperAdmin) {
      return;
    }

    this.citiesSubscription?.unsubscribe();
    this.cities = [];
    this.currencies = [];
    this.partnerForm.controls['city'].reset();
    this.partnerForm.controls['currency_id'].reset();

    this.citiesSubscription = this.partnerService.getCitiesByCountry(countryId).pipe(
      takeUntilDestroyed(this.destroyRef)
    ).subscribe({
      next: response => {
        this.cities = response.result;
        this.currencies = this.cities[0]?.country?.currencies ?? [];
      },
      error: error => this.toast.showError(error)
    });
  }

  submit(): void {
    if (!this.isSuperAdmin) {
      return;
    }

    if (this.partnerForm.invalid) {
      this.partnerForm.markAllAsTouched();
      this.toast.showError('Datos incompletos o invalidos, por favor verifique...');
      return;
    }

    const data = { ...this.partnerForm.value };
    if (data.bank !== '') {
      data.bank = { id: Number(data.bank) };
    } else {
      delete data.bank;
    }
    data.city = { id: Number(data.city) };
    data.country = { id: Number(data.country) };
    if (data.whatsappNumber !== '') {
      data.whatsappNumber = { id: Number(data.whatsappNumber) };
    } else {
      delete data.whatsappNumber;
    }

    this.spinner.open();
    this.partnerService.adminCreatePartner(data).subscribe({
      next: response => {
        this.spinner.close();
        if (response?.result?.credentialsEmailSent === false) {
          this.toast.showWarning(
            this.translate.instant('configurations.partners.credentialsNotSent')
          );
        } else {
          this.toast.showSuccess('Compañía creada correctamente');
        }
        this.router.navigate(['/my-portals']);
      },
      error: error => {
        this.spinner.close();
        if (error?.status === 409 || error?.error?.code === 'duplicate_access_email') {
          this.toast.showError(
            this.translate.instant('configurations.partners.duplicateAccessEmail')
          );
        } else {
          this.toast.showError(error);
        }
      }
    });
  }

  goBack(): void {
    this.router.navigate(['/my-portals']);
  }

  private loadCatalogs(): void {
    this.spinner.open();
    forkJoin({
      banks: this.partnerService.getBanks(-1),
      countries: this.partnerService.getCountries(),
      whatsappNumbers: this.partnerService.getWhatsappNumbers()
    }).pipe(
      finalize(() => this.spinner.close())
    ).subscribe({
      next: responses => {
        this.banks = responses.banks.result;
        this.countries = responses.countries.result;
        this.whatsappNumbers = responses.whatsappNumbers.result;
        this.partnerForm.controls['bank'].setValue(this.banks[0]?.id ?? '');
        this.partnerForm.controls['whatsappNumber'].setValue(this.whatsappNumbers[0]?.id ?? '');
      },
      error: error => this.toast.showError(error)
    });
  }
}
